import { _MiscMessages, ConfigSystemParams, DashboardProfit, MdmDashboardWidgetTitles, MdmDeviceClass } from 'src/app/core/consts';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, firstValueFrom, Observable, of, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { DashboardStatistic, DashboardStatistic_EmptyObject } from 'src/app/core/interfaces/general/dashboard-statistic';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { IntegerResultAndPercentageChangeDTO, IntegerResultAndPercentageChangeDTO_EmptyObject } from 'src/app/core/interfaces/general/integer-result-and-percentage-change';
import { MdmDevices_CompanyConfigService } from 'src/app/core/services/utelize/system/device-management/mdm-devices-company-config-service';
import { MdmDevicesIntuneStatsService } from 'src/app/core/services/utelize/system/device-management/mdm-devices-intune-stats-service';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { mdmUserDomainsDisplayText } from 'src/app/core/functions-generic/functions-generic-mdm';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { StringResultDTO, StringResultDTO_EmptyObject } from 'src/app/core/interfaces/general/string-result';
import { TippyToolTipService } from 'src/app/core/services/utelize/general/tippy-tool-tip-service';
import { WidgetService } from 'src/app/core/services/utelize/general/widget-service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-widget-row-mdm-devices-04',
  templateUrl: './widget-row-mdm-devices-04.component.html',
  styleUrls: ['./widget-row-mdm-devices-04.component.scss'],
  providers: [TippyToolTipService]
})

export class WidgetRowMdmDevices04Component implements AfterViewInit, OnChanges, OnDestroy, OnInit {

  @Input() actionButton0Show  : boolean | undefined;
  @Input() actionButton0Text  : string  | undefined;
  @Input() allowContainerClose: boolean | undefined;
  @Input() containerTitle     : string  | undefined;
  @Input() deviceClass        : string  | undefined;
  @Input() marginBottom       : string  | undefined="mb-0";
  @Input() previousSync       : boolean | undefined=false;
  @Input() useContainer       : boolean | undefined;
  @Input() showTitleHintText  : boolean | undefined;
  @Input() userDomains        : string  | undefined;
    
  @Output() actionButton0Click: EventEmitter<void                                     >=new EventEmitter<void                                     >();
  @Output() containerClosed   : EventEmitter<void                                     >=new EventEmitter<void                                     >();
  @Output() widgetClick       : EventEmitter<{widgetName: string, widgetValue: string}>=new EventEmitter<{widgetName: string, widgetValue: string}>();
  @Output() userDomainsClear  : EventEmitter<void                                     >=new EventEmitter<void                                     >();

  get isOtherClass   () { return this.deviceClass===MdmDeviceClass.other                            ; }
  get userDomainsText() { return mdmUserDomainsDisplayText(this.exceptionsService, this.userDomains); }
    
  get pollingWidgets                    () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.WidgetRow_Polling_MdmDevices04,                             false); }
  get showOldestDevice                  () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.WidgetRow_MdmDevices04_Show_OldestDevice,                   false); }
  get showUsersMoreThanOneDeviceSameType() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.WidgetRow_MdmDevices04_Show_UsersMoreThanOneDeviceSameType, false); }
    
  get maxWidgetsPerRow() { return this.configSystemService.getValueAsInteger(ConfigSystemParams.WidgetRow_MaxWidgetsPerRow_MdmDevices04,  7                            ); }
  get x_GbRemaining   () { return this.x_GbRemainingOverride ?? this.configSystemService.getValueAsInteger(ConfigSystemParams.MdmDashboardDevicesLess_X_GbRemaining, 12); }

  get statBoxMinWidth(): string {
    try {
      const raw          =this.maxWidgetsPerRow;
      const widgetsPerRow=(Number.isFinite(raw) && raw > 0) ? Math.max(1, Math.min(8, raw)) : 7;

      return (widgetsPerRow>=6) ? "180px" : "250px";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "250px";
    }
  }

  private filterCompanyID                                  : number | undefined;
  private filterDate                                       : OurDate | undefined=populateOurDate(this.exceptionsService, Date.now());
  private statTitleIntuneDevicesAverageAge                 : string="Average age (months)";
  private statTitleIntuneDevicesLess_X_GbRemaining         : string="Storage remaining";
  private statTitleIntuneDevicesOldest                     : string="Oldest device";
  private statTitleIntuneDevicesTotalNew                   : string="New devices";
  private statTitleIntuneDevicesWithMoreThanOneRegistration: string="Multi-registration";
  private statTitleIntuneDevicesXYearsOrOver               : string="Over 4 years old";
  private statTitleIntuneUsersWithMoreThanOneDevice        : string="Multi-device users";
  private statTitleIntuneUsersWithMoreThanOneDeviceSameType: string="Multi-device users";
  private storageRemainingCaption                          : string="";
  private x_GbRemainingOverride                            : number | undefined;
  
  private destroy$  =new Subject<void>();
  private selection$=new Subject<{ companyId: number; date: { year: number; month: number; day: number }; refreshToken?: number; }>();
  
  public expandContainer : boolean=true;
  public statData!       : Array<DashboardStatistic>;
  public syncInfoText    : string="";
  public visibleContainer: boolean=true;

  public readonly widgetNameSameType: string=MdmDashboardWidgetTitles.user_MoreThanOneDeviceSameType;

  constructor(
    private changeDetectorRef             : ChangeDetectorRef,
    private configSystemService           : ConfigSystemService,
    private exceptionsService             : ExceptionsService,
    private mdmDevicesCompanyConfigService: MdmDevices_CompanyConfigService,
    private mdmDevicesIntuneStatsService  : MdmDevicesIntuneStatsService,
    private tippyToolTipService           : TippyToolTipService,
    private widgetService                 : WidgetService
  ) {
    this.refreshStorageRemainingTitle();
  }

  public ngOnInit(): void {
    try {
      this.initSelectionStream();
      
      if (!this.containerTitle || this.containerTitle.trim()=="")
        this.containerTitle=_MiscMessages.TitleNotSet;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngOnChanges(changes: SimpleChanges): void {
    try {
      if (((changes['deviceClass'] && !changes['deviceClass'].firstChange)
           || (changes['userDomains'] && !changes['userDomains'].firstChange)
          ) && (this.filterCompanyID ?? -1)!=-1)
        this.refreshWidgets();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngAfterViewInit(): void {
    try {
      setTimeout(() => {
        this.displayData();
      });
      
      this.tippyToolTipService.setupBootstrapTooltips('[data-bs-toggle="tooltip"]', 'left');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngOnDestroy(): void {
    try {
      this.destroy$.next();
      this.destroy$.complete();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public refreshWidgets(): void {
    try {
      const companyId=this.filterCompanyID ?? -1;
      const date     ={
        year : <number>(this.filterDate?.year  ?? 1),
        month: <number>(this.filterDate?.month ?? 1),
        day  : <number>(this.filterDate?.day   ?? 1),
      };
  
      this.selection$.next({ companyId, date, refreshToken: Date.now() });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private displayData() {
    try {
      this.statData=[];
      
      let statIntuneDevicesOldest: DashboardStatistic=DashboardStatistic_EmptyObject();
                   
      let statIntuneDevicesAverageAge: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-history",
        iconColour    : "success",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : undefined,
        profit        : "",
        profitUpIsGood: false,
        showProfit    : false,
        showSpinner   : false,
        subTitle      : "Based on enrolled date",
        title         : this.statTitleIntuneDevicesAverageAge,
        value         : undefined,
        widgetName    : MdmDashboardWidgetTitles.device_AverageAge,
      }
                   
      let statIntuneDevicesLess_X_GbRemaining: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-hdd",
        iconColour    : "danger",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : undefined,
        profit        : "",
        profitUpIsGood: true,
        showProfit    : false,
        showSpinner   : false,
        subTitle      : this.storageRemainingCaption,
        title         : this.statTitleIntuneDevicesLess_X_GbRemaining,
        value         : undefined,
        widgetName    : MdmDashboardWidgetTitles.device_StorageRemaining,
      }
       
      if (this.showOldestDevice) {
        statIntuneDevicesOldest={
          decimalPlaces : 0,
          icon          : "bx-hourglass",
          iconColour    : "success",
          link          : undefined,
          linkText      : undefined,
          loading       : true,
          percentage    : undefined,
          profit        : "",
          profitUpIsGood: true,
          showProfit    : false,
          showSpinner   : false,
          subTitle      : "Based on enrolled date",
          title         : this.statTitleIntuneDevicesOldest,
          value         : undefined,
          widgetName    : MdmDashboardWidgetTitles.device_Oldest,
        }
      }
                   
      let statIntuneDevicesTotalNew: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-calendar-plus",
        iconColour    : "info",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : undefined,
        profit        : "",
        profitUpIsGood: true,
        showProfit    : false,
        showSpinner   : false,
        subTitle      : "Enrolled last 7 days",
        title         : this.statTitleIntuneDevicesTotalNew,
        value         : undefined,
        widgetName    : MdmDashboardWidgetTitles.device_TotalNew,
      }
                   
      let statIntuneDevicesXYearsOrOver: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-calendar-exclamation",
        iconColour    : "danger",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : undefined,
        profit        : "",
        profitUpIsGood: true,
        showProfit    : false,
        showSpinner   : false,
        subTitle      : "Devices",
        title         : this.statTitleIntuneDevicesXYearsOrOver,
        value         : undefined,
        widgetName    : MdmDashboardWidgetTitles.device_XYearsOrOver,
      }
                   
      let statIntuneUsersWithMoreThanOneDevice: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-devices",
        iconColour    : "warning",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : undefined,
        profit        : "",
        profitUpIsGood: true,
        showProfit    : false,
        showSpinner   : false,
        subTitle      : "Any device type",
        title         : this.statTitleIntuneUsersWithMoreThanOneDevice,
        value         : undefined,
        widgetName    : MdmDashboardWidgetTitles.user_MoreThanOneDevice,
      }
                   
      let statIntuneUsersWithMoreThanOneDeviceSameType: DashboardStatistic=DashboardStatistic_EmptyObject();

      if (this.showUsersMoreThanOneDeviceSameType) {
        statIntuneUsersWithMoreThanOneDeviceSameType={
          decimalPlaces : 0,
          icon          : "bx-copy-alt",
          iconColour    : "warning",
          link          : undefined,
          linkText      : undefined,
          loading       : true,
          percentage    : undefined,
          profit        : "",
          profitUpIsGood: false,
          showProfit    : false,
          showSpinner   : false,
          subTitle      : "Same device type",
          title         : this.statTitleIntuneUsersWithMoreThanOneDeviceSameType,
          value         : undefined,
          widgetName    : MdmDashboardWidgetTitles.user_MoreThanOneDeviceSameType,
        }
      }
                   
      let statIntuneDevicesWithMoreThanOneUser: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-duplicate",
        iconColour    : "warning",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : undefined,
        profit        : "",
        profitUpIsGood: true,
        showProfit    : false,
        showSpinner   : false,
        subTitle      : "Devices with more than 1",
        title         : this.statTitleIntuneDevicesWithMoreThanOneRegistration,
        value         : undefined,
        widgetName    : MdmDashboardWidgetTitles.device_MoreThanOneRegistration,
      }
                
      this.statData.push(statIntuneDevicesTotalNew);
      this.statData.push(statIntuneDevicesAverageAge);

      if (this.showOldestDevice)
        this.statData.push(statIntuneDevicesOldest);

      this.statData.push(statIntuneDevicesXYearsOrOver);
      this.statData.push(statIntuneDevicesLess_X_GbRemaining);
      this.statData.push(statIntuneDevicesWithMoreThanOneUser);
      this.statData.push(statIntuneUsersWithMoreThanOneDevice);

      if (this.showUsersMoreThanOneDeviceSameType)
        this.statData.push(statIntuneUsersWithMoreThanOneDeviceSameType);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
       return (undefined);
    }
  }
  
  private initSelectionStream(): void {
    try {
      this.selection$
      .pipe(
        debounceTime(150),
        distinctUntilChanged((a, b) =>
          a.companyId   ===b.companyId  &&
          a.date.year   ===b.date.year  &&
          a.date.month  ===b.date.month &&
          a.date.day    ===b.date.day   &&
          a.refreshToken===b.refreshToken
        ),
        switchMap(selected => {
          const companyId=selected.companyId;
          const year     =selected.date.year;
          const month    =selected.date.month;
          const day      =selected.date.day;
          
          const safeStart=<T>(observable: Observable<T>, fallback: T) =>
            observable.pipe(
              startWith(fallback),
              catchError(err => {
                this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(err)));
                return of(fallback);
              })
            );
                    
          const intuneDevicesAverageAgeSource=this.pollingWidgets
                                              ? this.widgetService.getIntuneDevicesAverageAgeUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                              : this.widgetService.getIntuneDevicesAverageAge       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains);

          const intuneDevicesLess_X_GbRemainingSource=this.pollingWidgets
                                                     ? this.widgetService.getIntuneDevicesLess_X_GbRemainingUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                                     : this.widgetService.getIntuneDevicesLess_X_GbRemaining       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains);

          const intuneDevicesOldestSource=this.showOldestDevice
                                          ? (
                                             this.pollingWidgets
                                             ? this.widgetService.getIntuneDevicesOldestUpdates(companyId, this.deviceClass, this.userDomains)
                                             : this.widgetService.getIntuneDevicesOldest       (companyId, this.deviceClass, this.userDomains)
                                            )
                                          : of(StringResultDTO_EmptyObject());

          const intuneDevicesTotalNewSource=this.pollingWidgets
                                            ? this.widgetService.getIntuneDevicesTotalNewUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                            : this.widgetService.getIntuneDevicesTotalNew       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains);

          const intuneDevicesWithMoreThanOneUserSource=this.pollingWidgets
                                                       ? this.widgetService.getIntuneDevicesWithMoreThanOneRegistrationUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                                       : this.widgetService.getIntuneDevicesWithMoreThanOneRegistration       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains);

          const intuneDevicesXYearsOrOverSource=this.pollingWidgets
                                                ? this.widgetService.getIntuneDevicesXYearsOrOverUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                                : this.widgetService.getIntuneDevicesXYearsOrOver       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains);
                    
          const intuneUsersWithMoreThanOneDeviceSource=this.pollingWidgets
                                                       ? this.widgetService.getIntuneUsersWithMoreThanOneDeviceUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                                       : this.widgetService.getIntuneUsersWithMoreThanOneDevice       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains);

          const intuneUsersWithMoreThanOneDeviceSameTypeSource=!this.showUsersMoreThanOneDeviceSameType
                                                               ? of(IntegerResultAndPercentageChangeDTO_EmptyObject())
                                                               : (this.pollingWidgets
                                                                  ? this.widgetService.getIntuneUsersWithMoreThanOneDeviceSameTypeUpdates(companyId, this.previousSync ?? false, this.deviceClass, this.userDomains)
                                                                  : this.widgetService.getIntuneUsersWithMoreThanOneDeviceSameType       (companyId, this.previousSync ?? false, this.deviceClass, this.userDomains));

          return combineLatest({
            intuneDevicesAverageAge                 : safeStart(intuneDevicesAverageAgeSource,                  IntegerResultAndPercentageChangeDTO_EmptyObject()),
            intuneDevicesLess_X_GbRemaining         : safeStart(intuneDevicesLess_X_GbRemainingSource,          IntegerResultAndPercentageChangeDTO_EmptyObject()),
            intuneDevicesWithMoreThanOneUser        : safeStart(intuneDevicesWithMoreThanOneUserSource,         IntegerResultAndPercentageChangeDTO_EmptyObject()),
            intuneDevicesOldest                     : safeStart(intuneDevicesOldestSource,                      StringResultDTO_EmptyObject()),
            intuneDevicesTotalNew                   : safeStart(intuneDevicesTotalNewSource,                    IntegerResultAndPercentageChangeDTO_EmptyObject()),
            intuneDevicesXYearsOrOver               : safeStart(intuneDevicesXYearsOrOverSource,                IntegerResultAndPercentageChangeDTO_EmptyObject()),
            intuneUsersWithMoreThanOneDevice        : safeStart(intuneUsersWithMoreThanOneDeviceSource,         IntegerResultAndPercentageChangeDTO_EmptyObject()),
            intuneUsersWithMoreThanOneDeviceSameType: safeStart(intuneUsersWithMoreThanOneDeviceSameTypeSource, IntegerResultAndPercentageChangeDTO_EmptyObject()),
          });
          // .pipe(
          //   catchError((error) => {
          //     this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(error)));
          //     return EMPTY; //👈 CRN_20260112 - If one fails, nothing is returned
          //   })
          // );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.applyIntuneDevicesAverageAge         (result.intuneDevicesAverageAge);
        this.applyIntuneDevicesLess_X_GbRemaining (result.intuneDevicesLess_X_GbRemaining);
        this.applyIntuneDevicesWithMoreThanOneUser(result.intuneDevicesWithMoreThanOneUser);

        if (this.showOldestDevice)
          this.applyIntuneDevicesOldest(result.intuneDevicesOldest);

        this.applyIntuneDevicesTotalNew           (result.intuneDevicesTotalNew);
        this.applyIntuneDevicesXYearsOrOver       (result.intuneDevicesXYearsOrOver);
        this.applyIntuneUsersWithMoreThanOneDevice(result.intuneUsersWithMoreThanOneDevice);

        if (this.showUsersMoreThanOneDeviceSameType)
          this.applyIntuneUsersWithMoreThanOneDeviceSameType(result.intuneUsersWithMoreThanOneDeviceSameType);

        this.changeDetectorRef.markForCheck();
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
    
  public async emitSelection(filterCompanyID: number | undefined, filterDate: OurDate | undefined): Promise<void> {
    try {
      this.filterCompanyID=filterCompanyID;
      this.filterDate     =filterDate;
      await this.setX_GbRemainingByCompany();

      const companyId=this.filterCompanyID ?? -1;
      const date     ={
        year : <number>(this.filterDate?.year  ?? 1),
        month: <number>(this.filterDate?.month ?? 1),
        day  : <number>(this.filterDate?.day   ?? 1),
      };
      
      this.selection$.next({ companyId, date });

      if (this.previousSync)
        this.getSyncedByInfo_Previous();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public getSyncedByInfo_Previous(): void {
    try {
      const companyID: number=this.filterCompanyID ?? 0;

      if (companyID<=0) {
        this.syncInfoText="";
        return;
      }

      this.mdmDevicesIntuneStatsService.getSyncedByInfo_Previous(companyID)
      .pipe(
        catchError((error) => {
          this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(error)));
          return of(null);
        })
      )
      .subscribe({
        next: data => {
          if (!data?.dateResult) {
            this.syncInfoText="Unsynced";
            this.changeDetectorRef.markForCheck();
            return;
          }

          const date     =new Date(data.dateResult);
          const pad      =(n: number) => n.toString().padStart(2, '0');
          const formatted=`${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ` +
                          `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

          this.syncInfoText=data?.stringResult
                            ? `${formatted} by ${data.stringResult}`
                            : `${formatted}`;
          this.changeDetectorRef.markForCheck();
        },
        error: error => {
          this.exceptionsService.handleException(new Error(error));
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyIntuneDevicesAverageAge(data: IntegerResultAndPercentageChangeDTO) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleIntuneDevicesAverageAge) {
          stat.loading   =false,
          stat.percentage=((this.filterCompanyID ?? -1)<=0) ? undefined : data?.percentageChange?.toString(),
          stat.profit    =data.percentageChange==null
                          ? undefined
                          : (data.percentageChange==0
                             ? DashboardProfit.Equal
                             : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up)),
          stat.showProfit=!this.previousSync && (this.filterCompanyID ?? -1)>0,
          stat.value     =data.result
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyIntuneDevicesLess_X_GbRemaining(data: IntegerResultAndPercentageChangeDTO) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.widgetName==MdmDashboardWidgetTitles.device_StorageRemaining) {
          stat.loading       =false,
          stat.percentage    =((this.filterCompanyID ?? -1)<=0) ? undefined : data?.percentageChange?.toString(),
          stat.profit        =data.percentageChange==null
                              ? undefined
                              : (data.percentageChange==0
                                 ? DashboardProfit.Equal
                                 : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up)),
          stat.profitUpIsGood=false;
          stat.showProfit    =!this.previousSync && (this.filterCompanyID ?? -1)>0,
          stat.value         =data.result;

          stat.iconColour=(Number(data.result) || 0)>0 ? "danger" : "success";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private async setX_GbRemainingByCompany(): Promise<void> {
    try {
      this.x_GbRemainingOverride=undefined;

      if ((this.filterCompanyID ?? -1)>0) {
        const companyConfigResult=await firstValueFrom(this.mdmDevicesCompanyConfigService.getByCompanyID(<number>this.filterCompanyID)
        .pipe(
          catchError(() => of(undefined))
        ));
        
        const companyConfig=Array.isArray(companyConfigResult) ? companyConfigResult[0] : companyConfigResult;
        const storageLess_X_GbRemaining=companyConfig?.storageLess_X_GbRemaining;

        if (storageLess_X_GbRemaining!=null)
          this.x_GbRemainingOverride=storageLess_X_GbRemaining;
      }

      this.refreshStorageRemainingTitle();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private refreshStorageRemainingTitle(): void {
    try {
      this.storageRemainingCaption=`Less than ${this.x_GbRemaining}GB`;

      const storageWidget=this.statData?.find(s => s.widgetName==MdmDashboardWidgetTitles.device_StorageRemaining);

      if (storageWidget)
        storageWidget.subTitle=this.storageRemainingCaption;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyIntuneDevicesWithMoreThanOneUser(data: IntegerResultAndPercentageChangeDTO) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleIntuneDevicesWithMoreThanOneRegistration) {
          stat.loading       =false,
          stat.percentage    =((this.filterCompanyID ?? -1)<=0) ? undefined : data?.percentageChange?.toString(),
          stat.profit        =data.percentageChange==null
                              ? undefined
                              : (data.percentageChange==0
                                 ? DashboardProfit.Equal
                                 : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up)),
          stat.profitUpIsGood=false;
          stat.showProfit    =!this.previousSync && (this.filterCompanyID ?? -1)>0,
          stat.value         =data.result;

          stat.iconColour=(Number(data.result) || 0)>0 ? "warning" : "success";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyIntuneDevicesOldest(data: StringResultDTO) {
    try {
      if (!this.showOldestDevice)
        return;
      
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleIntuneDevicesOldest) {
          stat.loading   =false,
          stat.percentage=undefined,
          stat.profit    =undefined,
          stat.value     =data.result
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyIntuneDevicesTotalNew(data: IntegerResultAndPercentageChangeDTO) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleIntuneDevicesTotalNew) {
          stat.loading   =false,
          stat.percentage=((this.filterCompanyID ?? -1)<=0) ? undefined : data?.percentageChange?.toString(),
          stat.profit    =data.percentageChange==null
                          ? undefined
                          : (data.percentageChange==0
                             ? DashboardProfit.Equal
                             : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up)),
          stat.showProfit=!this.previousSync && (this.filterCompanyID ?? -1)>0,
          stat.value     =data.result
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyIntuneDevicesXYearsOrOver(data: IntegerResultAndPercentageChangeDTO) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleIntuneDevicesXYearsOrOver) {
          stat.loading       =false,
          stat.percentage    =((this.filterCompanyID ?? -1)<=0) ? undefined : data?.percentageChange?.toString(),
          stat.profit        =data.percentageChange==null
                              ? undefined
                              : (data.percentageChange==0
                                 ? DashboardProfit.Equal
                                 : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up)),
          stat.profitUpIsGood=false;
          stat.showProfit    =!this.previousSync && (this.filterCompanyID ?? -1)>0,
          stat.value         =data.result;

          stat.iconColour=(Number(data.result) || 0)>0 ? "danger" : "success";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyIntuneUsersWithMoreThanOneDevice(data: IntegerResultAndPercentageChangeDTO) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.widgetName==MdmDashboardWidgetTitles.user_MoreThanOneDevice) {
          stat.loading       =false,
          stat.percentage    =((this.filterCompanyID ?? -1)<=0) ? undefined : data?.percentageChange?.toString(),
          stat.profit        =data.percentageChange==null
                              ? undefined
                              : (data.percentageChange==0
                                 ? DashboardProfit.Equal
                                 : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up)),
          stat.profitUpIsGood=false;
          stat.showProfit    =!this.previousSync && (this.filterCompanyID ?? -1)>0,
          stat.value         =data.result;

          stat.iconColour=(Number(data.result) || 0)>0 ? "warning" : "success";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyIntuneUsersWithMoreThanOneDeviceSameType(data: IntegerResultAndPercentageChangeDTO) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.widgetName==MdmDashboardWidgetTitles.user_MoreThanOneDeviceSameType) {
          stat.loading       =false,
          stat.percentage    =((this.filterCompanyID ?? -1)<=0) ? undefined : data?.percentageChange?.toString(),
          stat.profit        =data.percentageChange==null
                              ? undefined
                              : (data.percentageChange==0
                                 ? DashboardProfit.Equal
                                 : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up)),
          stat.profitUpIsGood=false;
          stat.showProfit    =!this.previousSync && (this.filterCompanyID ?? -1)>0,
          stat.value         =data.result;

          stat.iconColour=(Number(data.result) || 0)>0 ? "warning" : "success";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public trendPercent(stat: DashboardStatistic): number | null {
    try {
      if (!stat?.showProfit || stat?.percentage==null || stat.percentage=="")
        return null;

      const percent=Number(stat.percentage);

      return Number.isFinite(percent) ? percent : null;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return null;
    }
  }

  public onWidgetClick(stat: DashboardStatistic): void {
    try {
      const widgetName =stat?.widgetName ?? stat?.title ?? "";
      const widgetValue=(stat?.value ?? "") as any;

      // alert(`WidgetRowMdmDevices04Component\nWidgetName: ${widgetName}\nWidgetValue: ${widgetValue}`);
      
      this.widgetClick.emit({ widgetName: widgetName, widgetValue: "" + widgetValue });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public actionButton0OnClick(): void {
    try {
      this.actionButton0Click.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public collapseContainer(): void {
    try {
      this.expandContainer=false;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public closeContainer(): void {
    try {
      this.visibleContainer=false;
      this.containerClosed.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public restoreContainer(): void {
    try {
      this.visibleContainer=true;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
